import 'whatwg-fetch';
import planck from 'planck-js';
import * as PIXI from 'pixi.js';
import GSObject from './Base/GSObject';
import GSOUUIDArray from './Base/GSOUUIDArray';
import Level from './Level';
import Vector from './Base/Math/Vector';
import Global from './Global';
import { GameGoalFlag, GameScoreEnum } from './Enums';
import PlayerSettings from './PlayerSettings';
import AssetManager from './Base/AssetManager';
import Loader from './Base/Loader';
import LibraryManager from './Base/Library/LibraryManager';

import settings from '../settings';

window.LibraryManager = LibraryManager;

export const ViewPortScaling = {
    NativeResolution: 0,
    Cover: 1,
    Contain: 2,
    GamePlayBest: 3
};

export default class Game extends GSObject {
    constructor() {
        super(null);

        this.gameID = null;

        this.pixiApp = null;
        this.title = 'Mijn Game';
        this.description = 'Spel omschrijving';
        this.goalSetting = GameGoalFlag.FINISH;
        this.scoreSetting = GameScoreEnum.NONE;

        this.playerSettings = new PlayerSettings(this);

        this.pixiContainer = null;
        this.screenshotFlashSprite = null;

        this.physicsWorld = null;

        this.levels = new GSOUUIDArray(this, Level);
        this.activeLevel = null;

        // Time since the game started running.
        this.time = 0;
        this.deltaTime = 0;

        this.lastUpdate = 0;
        this.fixedUpdateDelta = 0;
        this.lastFixedUpdate = 0;
        this.fixedUpdateRate = (1 / 30) * 1000; // Run the physics and gameplay update at 30
        this.totalTime = 0;
        this.totalFrames = 0;

        this.scaleModifier = 1;
        this.elementScaleModifier = 1;
        this.elementResolutionModifier = 1;
        this.runningResolution = 1;

        this.isGameClass = true;

        this.activeCanvasUIObject = null;
        this.hoverCanvasUIObject = null;
        this.evtOnTouchDown = this.onTouchDown.bind(this);
        this.evtOnTouchMove = this.onTouchMove.bind(this);
        this.evtOnTouchUp = this.onTouchUp.bind(this);
        this.evtOnMouseDown = this.onCanvasMouseDown.bind(this);
        this.evtOnMouseMove = this.onCanvasMouseMove.bind(this);
        this.evtOnMouseUp = this.onCanvasMouseUp.bind(this);
        this.evtOnKeyDown = this.onCanvasKeyDown.bind(this);
        this.evtOnKeyUp = this.onCanvasKeyUp.bind(this);
        this.mousePosition = new Vector();
        this.keys = {};
        this.upKeys = {};
        this._upKeys = [];
        this.downKeys = {};
        this._downKeys = [];
        this.inputVector = new Vector(0);
        this.evtOnResize = this.updateRenderSize.bind(this);

        this.disableLerping = false;

        this.viewPortScaling = ViewPortScaling.NativeResolution;

        this.settings = {};

        this.paused = false;
        this.onMobile = false;

        // For debug purposes only:
        window.game = this;
    }

    init(settingsPath) {
        const loader = new Loader();

        loader.addStage('Game Studio instellingen ophalen', 1, (finish, error, update) => {
            fetch(settingsPath, {
                method: 'GET',
                cache: 'no-cache'
            })
                .then(response => {
                    if (response.status >= 300) {
                        error(`Failed to retrieve settings.json. ${response.status}`);
                        return;
                    }
                    return response;
                })
                .then(reponse => {
                    return reponse.json();
                })
                .then(data => {
                    this.settings = data;
                    finish();
                });
        });

        loader.addStage('Bibliotheek ophalen', 1, (finish, error, update) => {
            fetch(this.settings.library, {
                method: 'GET',
                cache: 'no-cache'
            })
                .then(response => {
                    if (response.status >= 300) {
                        error(`Failed to retrieve library.json. ${response.status}`);
                        return;
                    }
                    return response;
                })
                .then(reponse => {
                    return reponse.json();
                })
                .then(data => {
                    LibraryManager.fill(loader, data, this.settings);
                    finish();
                });

            // Use this stage to pre-load some icons
            const preloadImages = [
                'icons/fullscreen.png',
                'icons/smaller-screen.png',
                'icons/pause.png',
                'icons/play.png',
                'icons/mute.png',
                'icons/no-mute.png',
                'play-audio.png',
                'stop-audio.png',
                'icons/radiobutton.png',
                'icons/radiobutton_checked.png',
                'icons/checkbox.png',
                'icons/checkbox_checked.png'
            ];
            for (let i = 0; i < preloadImages.length; ++i) {
                const img = new Image();
                img.src = `${settings.assetsPath}img/${preloadImages[i]}`;
            }
        });

        const playerSprites = [
            { id: 'img.player_overlay', url: `${settings.assetsPath}img/player_overlay.png` },
            { id: 'font.retro_gaming', url: `${settings.assetsPath}fonts/retro_gaming.ttf` },
            { id: 'img.particle_coin', url: `${settings.assetsPath}img/particles/coin.png` },
            { id: 'img.particle_hearth', url: `${settings.assetsPath}img/particles/hearth.png` },
            { id: 'img.white', url: `${settings.assetsPath}img/white.png` }
        ];

        loader.addStage('Speler plaatjes ophalen', playerSprites.length, (finish, error, update) => {
            const assetLoaderInfo = AssetManager.loadDatabase(playerSprites, () => {
                finish();
            });
            assetLoaderInfo.onLoaded = () => {
                if (assetLoaderInfo.errors.length > 0) {
                    error(assetLoaderInfo.errors);
                } else update(assetLoaderInfo.loadCount);
            };
        });

        return loader;
    }

    setResolution(resolution) {
        this.runningResolution = resolution;
        this.elementResolutionModifier = 1 / resolution;
        this.setScale(resolution, resolution);
    }

    setScale(viewScale, elementScale) {
        const curE = this.elementScaleModifier;
        const curV = this.scaleModifier;

        this.elementScaleModifier = elementScale;
        this.scaleModifier = viewScale;

        if (curE !== this.elementScaleModifier || curV !== this.scaleModifier) this.updateRenderSize();
    }

    static credits() {
        console.log('De GameKit en GamePlayer zijn gemaakt door Olivier Wervers, Meine Zeinstra en Stefan de Raadt voor de NTR.');
        console.log('OLLES (Leisure and Entertainment Software): http://olles.nl/');
        console.log('Olivier Wervers: https://oli414.com/');
    }

    // Set the (PIXI) render application. Required before game can be rendered.
    setRenderApplication(pixiApp) {
        this.pixiApp = pixiApp;
        this.pixiApp.view.addEventListener('mousedown', this.evtOnMouseDown, false);
        window.addEventListener('mousemove', this.evtOnMouseMove, false);
        window.addEventListener('keydown', this.evtOnKeyDown, false);
        window.addEventListener('keyup', this.evtOnKeyUp);
        window.addEventListener('resize', this.evtOnResize);
        const self = this;
        window.addEventListener('orientationchange', () => {
            // Orientation event needs a second to cause the canvas parent element to rescale as well.
            window.setTimeout(self.evtOnResize, 500);
        });

        this.pixiApp.view.addEventListener('touchstart', this.evtOnTouchDown, false);
        this.pixiApp.view.addEventListener('touchmove', this.evtOnTouchMove, false);
        this.pixiApp.view.addEventListener('touchend', this.evtOnTouchUp, false);
        this.pixiApp.view.addEventListener('touchcancel', this.evtOnTouchUp, false);

        this.pixiApp.view.tabIndex = 1;
    }

    setViewPortScaling(scaling) {
        this.viewPortScaling = scaling;
        this.updateRenderSize();
    }

    // Returns the view size within the context of the game.
    getViewSize() {
        const size = new Vector(this.pixiApp.renderer.width, this.pixiApp.renderer.height);
        size.x /= this.pixiContainer.scale.x;
        size.y /= this.pixiContainer.scale.y;
        return size;
    }

    updateRenderSize() {
        this.totalFrames = 0;
        this.totalTime = 0;

        if (!this.pixiApp) return;
        if (!this.pixiContainer) return;

        const canvasWrapper = this.pixiApp.view.parentElement;

        let { scaleModifier } = this;
        let internalViewPortScaling = this.viewPortScaling;
        if (this.viewPortScaling === ViewPortScaling.GamePlayBest) {
            let scale = 1;
            const canvasWrapperRatio = canvasWrapper.clientWidth / canvasWrapper.clientHeight;
            if (canvasWrapperRatio < Global.NATIVE_RATIO) scale = canvasWrapper.clientHeight / (Global.NATIVE_BLOCK_SIZE * Global.NATIVE_LEVEL_HEIGHT);
            else scale = canvasWrapper.clientWidth / (Global.NATIVE_BLOCK_SIZE * Global.NATIVE_LEVEL_WIDTH);

            const difference = new Vector();
            difference.x = (scale * Global.NATIVE_LEVEL_WIDTH * Global.NATIVE_BLOCK_SIZE - canvasWrapper.clientWidth) / Global.NATIVE_BLOCK_SIZE / scale;
            difference.y = (scale * Global.NATIVE_LEVEL_HEIGHT * Global.NATIVE_BLOCK_SIZE - canvasWrapper.clientHeight) / Global.NATIVE_BLOCK_SIZE / scale;

            internalViewPortScaling = ViewPortScaling.Cover;
            if (difference.x < -Global.MAX_VIEW_W_DIFFERENCE) internalViewPortScaling = ViewPortScaling.Contain;
            if (difference.x > Global.MAX_VIEW_W_DIFFERENCE) internalViewPortScaling = ViewPortScaling.Contain;
            if (difference.y < -Global.MAX_VIEW_H_DIFFERENCE) internalViewPortScaling = ViewPortScaling.Contain;
            if (difference.y > Global.MAX_VIEW_H_DIFFERENCE) internalViewPortScaling = ViewPortScaling.Contain;
            scaleModifier = 1;
        }

        if (internalViewPortScaling === ViewPortScaling.Cover || internalViewPortScaling === ViewPortScaling.NativeResolution) {
            this.pixiApp.renderer.resize(canvasWrapper.clientWidth * this.elementScaleModifier, canvasWrapper.clientHeight * this.elementScaleModifier);

            let scale = 1;
            if (internalViewPortScaling === ViewPortScaling.Cover) {
                const canvasWrapperRatio = canvasWrapper.clientWidth / canvasWrapper.clientHeight;
                if (canvasWrapperRatio < Global.NATIVE_RATIO) scale = this.pixiApp.renderer.height / (Global.NATIVE_BLOCK_SIZE * Global.NATIVE_LEVEL_HEIGHT);
                else scale = this.pixiApp.renderer.width / (Global.NATIVE_BLOCK_SIZE * Global.NATIVE_LEVEL_WIDTH);
            }
            this.pixiContainer.scale.set(scale * scaleModifier);
        } else {
            const canvasWrapperRatio = canvasWrapper.clientWidth / canvasWrapper.clientHeight;
            if (canvasWrapperRatio < Global.NATIVE_RATIO)
                this.pixiApp.renderer.resize(canvasWrapper.clientWidth * this.elementScaleModifier, (canvasWrapper.clientWidth / Global.NATIVE_RATIO) * this.elementScaleModifier);
            else this.pixiApp.renderer.resize(canvasWrapper.clientHeight * Global.NATIVE_RATIO * this.elementScaleModifier, canvasWrapper.clientHeight * this.elementScaleModifier);
            const scale = this.pixiApp.renderer.height / (Global.NATIVE_BLOCK_SIZE * Global.NATIVE_LEVEL_HEIGHT);
            this.pixiContainer.scale.set(scale * scaleModifier);
        }

        this.pixiApp.view.style.width = `${this.pixiApp.renderer.width * this.elementResolutionModifier}px`;
        this.pixiApp.view.style.height = `${this.pixiApp.renderer.height * this.elementResolutionModifier}px`;
    }

    // Update the visuals and render the canvas
    _render() {
        if (!this._hasVisuals) throw new Error('Visuals have not yet been created. Please call createVisuals once before rendering.');
        this._updateVisuals();
        this.pixiApp.render();
    }

    // Update the game
    update() {
        if (this.lastUpdate === 0) this.lastUpdate = Date.now();

        const now = Date.now();
        this.deltaTime = now - this.lastUpdate;
        // Limit deltaTime to a minimum of 10 fps
        if (this.deltaTime > 100) this.deltaTime = 100;
        else this.totalTime += this.deltaTime;

        this.fixedUpdateDelta += this.deltaTime;
        this.time += this.deltaTime;

        if (this._inPlayMode && this._hasBody && !this.paused) {
            window.GamePlayer.instance.modifyTime(this.deltaTime);
        }

        if (this.fixedUpdateDelta > this.fixedUpdateRate) {
            this.totalFrames++;
            let updatesCount = 0;
            this.lastFixedUpdate = now;
            while (this.fixedUpdateDelta > this.fixedUpdateRate - 5) {
                this._fixedUpdate();
                updatesCount++;
                this.fixedUpdateDelta -= this.fixedUpdateRate;
                if (updatesCount === 10) {
                    console.warn("Can't keep up. Dropping frames. Game may run more slowly.");
                    this.fixedUpdateDelta = 0;
                    this.lastUpdate = now;
                    break;
                }
            }
        }
        this._update(this.deltaTime);
        if (this.totalTime > 1000) {
            // Performance agent.
            if (this.totalFrames / (this.totalTime / 1000) < 15) {
                if (this.runningResolution > 0.8) {
                    this.setResolution(this.runningResolution - 0.1);
                    console.warn('Decreasing resolution for better performance.');
                }
            }
            if (this.totalFrames / (this.totalTime / 1000) > 30) {
                if (this.runningResolution < 1) {
                    this.setResolution(this.runningResolution + 0.1);
                    console.warn('Increasing resolution back up since performance is good.');
                }
            }
            if (this.totalTime > 2000) {
                this.totalFrames = 0;
                this.totalTime = 0;
            }
        }
        this._render();

        this.lastUpdate = now;
    }

    getPixiContainer() {
        if (!this.pixiApp) throw new Error('Failed to retrieve PIXI stage. No PIXI application was set up for this game.');
        return this.pixiContainer;
    }

    // Makes this object have visual presence in the world.
    createVisuals() {
        this._gsoCreateVisuals();
    }

    // Update the visual presence in the world.
    _updateVisuals() {
        // this.pixiApp.stage.scale.x = this.visualScale;
        // this.pixiApp.stage.scale.y = this.visualScale;

        this._gsoUpdateVisuals();
    }

    // Removes this object's visual presence from the world.
    destroyVisuals() {
        this._gsoDestroyVisuals();
    }

    onCreateVisuals() {
        this.pixiContainer = new PIXI.Container();

        this.screenshotFlashSprite = new PIXI.Sprite(AssetManager.get('img.white'));
        this.screenshotFlashSprite.alpha = 0;
        this.screenshotFlashSprite.position.x = 0;
        this.screenshotFlashSprite.position.y = 0;

        this.pixiApp.stage.addChild(this.pixiContainer);
        this.pixiApp.stage.addChild(this.screenshotFlashSprite);
        this.updateRenderSize();
    }

    onUpdateVisuals() {
        this.screenshotFlashSprite.width = this.pixiApp.screen.width;
        this.screenshotFlashSprite.height = this.pixiApp.screen.height;
        if (this.screenshotFlashSprite.alpha > 0) {
            this.screenshotFlashSprite.alpha -= (this.deltaTime / 1000) * 8;
            if (this.screenshotFlashSprite.alpha < 0) {
                this.screenshotFlashSprite.alpha = 0;
            }
        }
    }

    onDestroyVisuals() {
        this.screenshotFlashSprite.destroy();
        this.screenshotFlashSprite = null;
        this.pixiContainer.destroy();
    }

    // Create physics bodies.
    createBody() {
        this.physicsWorld = planck.World(planck.Vec2(0, 9.8));
        this._gsoCreateBody(this.physicsWorld);
    }

    // Destroy physics bodies.
    destroyBody() {
        this._gsoDestroyBody(this.physicsWorld);
    }

    onDestroyBody() {
        this.physicsWorld = null;
    }

    // Updated every tick.
    _update(deltaTime) {
        for (let i = 0; i < this._downKeys.length; i++) {
            this.keys[this._downKeys[i]] = true;
        }
        this._downKeys = [];
        for (let i = 0; i < this._upKeys.length; i++) {
            this.keys[this._upKeys[i]] = false;
        }
        this._upKeys = [];

        this._gsoUpdate(deltaTime);

        this.upKeys = {};
        this.downKeys = {};

        this._updateCanvasUI();
    }

    _updateCanvasUI() {
        if (this.activeCanvasUIObject) {
            // Update onMouseMove at the same rate as tick rate instead of event rate.
            this.activeCanvasUIObject.onMouseMove(this.mousePosition);
            this.hoverCanvasUIObject = this.activeCanvasUIObject;
        } else {
            if (this.hoverCanvasUIObject) this.hoverCanvasUIObject.state = 0;
            const match = this._getRelevantUI(this.mousePosition, false);
            if (this.hoverCanvasUIObject !== match.item) {
                if (this.hoverCanvasUIObject) {
                    if (this.hoverCanvasUIObject.onHoverExit) this.hoverCanvasUIObject.onHoverExit();
                }
                if (match.item) {
                    if (match.item.onHoverEnter) match.item.onHoverEnter();
                }
            }
            if (match.item) {
                this.hoverCanvasUIObject = match.item;
                this.hoverCanvasUIObject.state = 1;
                if (this.hoverCanvasUIObject.onHoverMove) this.hoverCanvasUIObject.onHoverMove(this.mousePosition);
            } else this.hoverCanvasUIObject = null;
        }
    }

    // Updated at a fixed rate (together with physics).
    _fixedUpdate() {
        if (this._hasBody && !this.paused) this.physicsWorld.step(this.fixedUpdateRate / 1000, 8, 3);
        this._gsoFixedUpdate(this.fixedUpdateRate);
        if (this.activeLevel) {
            if (this.activeLevel.finished) {
                if (this._hasBody && !this.paused) {
                    this.paused = true;
                    if (this.activeLevel.onFinished) this.activeLevel.onFinished(this.activeLevel.upcomingLevel);
                    else console.error('Level is finished but no callback was assigned.');
                }
            }
        }
    }

    load(gameID, onSuccess, onError, forceAuthentication = false) {
        window.LoginAPI.getHash(authHash => {
            let cancelled = false;
            fetch(`${this.settings.api.load}?gameID=${gameID}${forceAuthentication ? '&forceAuthentication=1' : ''}`, {
                method: 'GET',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authHash
                }
            })
                .then(response => {
                    if (!response.ok || response.status >= 300) {
                        if (response.status === 401) {
                            console.error('Load game failed. Game is made by a different user and has not been published.');
                            if (onError) {
                                onError(response.statusText);
                            }
                        } else {
                            console.error(`Load game failed. Server responded with status code ${response.status}: ${response.statusText}`);
                            if (onError) {
                                onError(response.statusText);
                            }
                        }
                        cancelled = true;
                        return;
                    }
                    return response.json();
                })
                .catch(err => {
                    console.error(`Authorization failed. Server failed to respond.`);
                    if (onError) {
                        onError('No response');
                    }
                    cancelled = true;
                })
                .then(json => {
                    if (!cancelled) {
                        this.deserialize(json.gameData);
                        if (json.gameID) this.gameID = json.gameID;

                        if (onSuccess) onSuccess(json);
                    }
                });
        });
    }

    // Lerp based on deltaTime within the context of a fixed time step.
    deltaLerp(a, b) {
        if (this.disableLerping) return b;
        const range = this.lastFixedUpdate + this.fixedUpdateRate - this.lastUpdate;
        const t = this.deltaTime / range;
        return (1 - t) * a + t * b;
    }

    _shortAngleDist(a0, a1) {
        const max = Math.PI * 2;
        const da = (a1 - a0) % max;
        return ((2 * da) % max) - da;
    }

    // Angle lerp based on deltaTime within the context of a fixed time step.
    angleDeltaLerp(a, b) {
        if (this.disableLerping) return b;
        const range = this.lastFixedUpdate + this.fixedUpdateRate - this.lastUpdate;
        const t = this.deltaTime / range;
        return a + this._shortAngleDist(a, b) * t;
    }

    canvasToGamePosition(canvasPosition, accountForLevelView = true) {
        const pixiPoint = new PIXI.Point();
        if (accountForLevelView && this.activeLevel) this.activeLevel.pixiContainer.worldTransform.applyInverse(canvasPosition.makePIXIPoint(), pixiPoint);
        else this.pixiContainer.worldTransform.applyInverse(canvasPosition.makePIXIPoint(), pixiPoint);
        return Vector.fromPIXIPoint(pixiPoint);
    }

    onCanvasKeyDown(evt) {
        if ((evt.srcElement.nodeName === 'INPUT' && (evt.srcElement.type === 'text' || evt.srcElement.type === 'number')) || evt.srcElement.nodeName === 'TEXTAREA') {
            return;
        }

        if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp' || evt.key === 'ArrowRight' || evt.key === 'ArrowDown' || evt.key === ' ') evt.preventDefault();

        if (!this.keys[evt.key]) {
            this._downKeys.push(evt.key);
            this.downKeys[evt.key] = true;
        }
        if (this.keys.Control && evt.key === 'd') {
            // disable chrome's bookmark shortcut
            evt.preventDefault();
            evt.stopPropagation();
        }
        if (evt.key === ' ') {
            evt.preventDefault();
        }
    }

    onCanvasKeyUp(evt) {
        this._upKeys.push(evt.key);
        this.upKeys[evt.key] = true;
    }

    onTouchDown(evt) {
        this.onMobile = true;

        const touchobj = evt.changedTouches[0];
        const evtNew = evt;
        evtNew.clientX = touchobj.clientX;
        evtNew.clientY = touchobj.clientY;
        evtNew.isTouch = true;
        evtNew.button = 0;

        const mousePosition = this.getMouseEventPosition(evtNew);
        this.mousePosition = mousePosition;
        const match = this._getRelevantUI(mousePosition, true);

        if (!match.item) return;
        match.item.onHoverEnter();
        match.item.onHoverMove(mousePosition);

        this.onCanvasMouseDown(evtNew);
    }

    onTouchMove(evt) {
        const touchobj = evt.changedTouches[0];
        const evtNew = evt;
        evtNew.clientX = touchobj.clientX;
        evtNew.clientY = touchobj.clientY;
        evtNew.isTouch = true;
        evtNew.button = 0;
        this.onCanvasMouseMove(evtNew);
    }

    onTouchUp(evt) {
        const touchobj = evt.changedTouches[0];
        const evtNew = evt;
        evtNew.clientX = touchobj.clientX;
        evtNew.clientY = touchobj.clientY;
        evtNew.button = 0;
        evtNew.isTouch = true;
        this.onCanvasMouseUp(evtNew);
    }

    onCanvasMouseDown(evt) {
        if (evt.button !== 0) return;
        evt.preventDefault();

        this.pixiApp.view.focus();

        const mousePosition = this.getMouseEventPosition(evt);
        this.mousePosition = mousePosition;
        const match = this._getRelevantUI(mousePosition, true);

        if (!match.item) return;

        this.activeCanvasUIObject = match.item;
        this.activeCanvasUIObject.state = 2;
        this.activeCanvasUIObject.onMouseDown(mousePosition);

        if (!evt.isTouch) {
            window.addEventListener('mouseup', this.evtOnMouseUp, false);
        }
    }

    onCanvasMouseMove(evt) {
        if (evt.button !== 0) return;
        const mousePosition = this.getMouseEventPosition(evt);
        this.mousePosition = mousePosition;

        // evt.preventDefault();
    }

    onCanvasMouseUp(evt) {
        if (evt.button !== 0) return;

        const mousePosition = this.getMouseEventPosition(evt);
        this.mousePosition = mousePosition;
        if (this.activeCanvasUIObject) {
            this.activeCanvasUIObject.state = 0;
            this.activeCanvasUIObject.onMouseUp(mousePosition);
            this.activeCanvasUIObject = null;
        }

        if (!evt.isTouch) {
            window.removeEventListener('mouseup', this.evtOnMouseUp, false);
        }
    }

    getMouseEventPosition(evt) {
        const rect = this.pixiApp.view.getBoundingClientRect();
        return new Vector((evt.clientX - rect.left) / this.elementResolutionModifier, (evt.clientY - rect.top) / this.elementResolutionModifier);
    }

    isCanvas() {
        return this.pixiApp.renderer instanceof PIXI.CanvasRenderer;
    }

    isMobile() {
        return this.onMobile;
    }
}

Game.serializeableFields = {
    title: String,
    description: String,
    levels: [Level],
    goalSetting: Number,
    scoreSetting: Number,
    playerSettings: PlayerSettings
};
